import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  BumpRecipeVersionInput,
  CreateRecipeInput,
  ListResponse,
  Recipe,
} from "./types";

const base = "/v1/recipes";

export function useRecipes() {
  return useQuery({
    queryKey: queryKeys.recipes.list(),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Recipe>>(base, undefined, signal),
  });
}

export function useRecipe(id: string | undefined, version?: number) {
  return useQuery({
    queryKey: id ? queryKeys.recipes.detail(id, version) : queryKeys.recipes.all,
    queryFn: ({ signal }) =>
      api.get<Recipe>(
        `${base}/${id}`,
        version === undefined ? undefined : { version },
        signal,
      ),
    enabled: !!id,
  });
}

export function useCreateRecipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRecipeInput) => api.post<Recipe>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.recipes.all });
      qc.setQueryData(queryKeys.recipes.detail(created.id), created);
    },
  });
}

export function useBumpRecipeVersion(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BumpRecipeVersionInput) =>
      api.post<Recipe>(`${base}/${id}/versions`, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.recipes.all });
      qc.setQueryData(queryKeys.recipes.detail(id), created);
    },
  });
}

export function useArchiveRecipe(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recipes.all });
    },
  });
}
